/**
 * Utility helpers for building the combined, sorted timeline
 * that is displayed in a chat window.
 *
 * The backend already returns items ordered chronologically within each chat,
 * but this module owns the rules for merging entries, computing the unread
 * divider position, and type-narrowing.
 */

import type {ChatTimelineEntryDTO} from "../types";
import {isMessageEntry, isStatusPillEntry, MessageKind} from "../types";

/**
 * Represents a timeline entry that has been decorated with additional
 * display metadata (e.g. whether the unread divider sits below this entry,
 * or — for the very first entry — sits above it).
 */
export interface DecoratedTimelineEntry {
    entry: ChatTimelineEntryDTO;
    /**
     * When true, the "unread divider" should be rendered directly after this entry.
     * Mirrors the backend predicate {@code NotificationsChatMessageRepository.countUnreadInChat}:
     * only USER_MESSAGE entries authored by another company count as "unread".
     */
    hasUnreadDividerBelow: boolean;
    /**
     * When true, the "unread divider" should be rendered directly BEFORE this entry
     * (i.e. this is the very first unread foreign message and there is no predecessor entry).
     * At most one entry in the decorated list has this flag set to true.
     */
    hasUnreadDividerAbove: boolean;
}

/**
 * Merges two already-sorted timeline arrays (messages and status pills) into a
 * single chronologically sorted list.
 *
 * Both arrays are expected to be sorted ascending by timestamp.  The merge is a
 * standard two-pointer merge so it runs in O(n + m) time.
 *
 * @param a - first sorted timeline slice
 * @param b - second sorted timeline slice
 * @returns merged array sorted ascending by timestamp
 */
export function mergeTimelines(
    a: ChatTimelineEntryDTO[],
    b: ChatTimelineEntryDTO[]
): ChatTimelineEntryDTO[] {
    const result: ChatTimelineEntryDTO[] = [];
    let i = 0;
    let j = 0;

    while (i < a.length && j < b.length) {
        const tA = getTimestamp(a[i]);
        const tB = getTimestamp(b[j]);
        if (tA <= tB) {
            result.push(a[i]);
            i += 1;
        } else {
            result.push(b[j]);
            j += 1;
        }
    }

    while (i < a.length) {
        result.push(a[i]);
        i += 1;
    }
    while (j < b.length) {
        result.push(b[j]);
        j += 1;
    }

    return result;
}

/**
 * Decorates a timeline (already in chronological order) by placing the
 * "unread divider" marker before the first unread foreign message.
 *
 * An entry counts as "first unread" when ALL of the following hold:
 *  1. It is a MESSAGE entry (not a STATUS_PILL).
 *  2. Its {@code message.kind} is {@code "USER_MESSAGE"} (not a {@code "SYSTEM_NOTE"}).
 *  3. Its {@code message.read} is not {@code true} — uses {@code !== true} (not {@code === false})
 *     for defensive handling of {@code null}/{@code undefined}.
 *
 * Note: SYSTEM_NOTEs are never considered for the divider regardless of their {@code read} value,
 * because the backend always sets {@code read = true} for them implicitly.
 *
 * Placement rules:
 * - If the first qualifying entry has a predecessor: set {@code hasUnreadDividerBelow = true}
 *   on the predecessor entry (divider renders between predecessor and the unread entry).
 * - If the first qualifying entry is at index 0 (no predecessor): set
 *   {@code hasUnreadDividerAbove = true} on that entry itself.
 * - If no qualifying entry exists: all flags remain false (no divider is rendered).
 *
 * @param timeline - ordered timeline from the chat
 * @returns        array of {@link DecoratedTimelineEntry}
 */
export function decorateWithUnreadDivider(
    timeline: ChatTimelineEntryDTO[]
): DecoratedTimelineEntry[] {
    if (timeline.length === 0) {
        return [];
    }

    // Find the index of the first USER_MESSAGE with read !== true.
    let firstUnreadIndex = -1;
    for (let i = 0; i < timeline.length; i++) {
        const entry = timeline[i];

        if (!isMessageEntry(entry)) {
            continue;
        }

        if (entry.message.kind !== MessageKind.USER_MESSAGE) {
            continue;
        }

        // Use !== true (not === false) for defensive handling of null/undefined.
        if (entry.message.read !== true) {
            firstUnreadIndex = i;
            break;
        }
    }

    return timeline.map((entry, index) => {
        const base: DecoratedTimelineEntry = {entry, hasUnreadDividerBelow: false, hasUnreadDividerAbove: false};

        if (firstUnreadIndex === -1) {
            return base;
        }

        if (firstUnreadIndex > 0 && index === firstUnreadIndex - 1) {
            return {...base, hasUnreadDividerBelow: true};
        }

        if (firstUnreadIndex === 0 && index === 0) {
            return {...base, hasUnreadDividerAbove: true};
        }

        return base;
    });
}

/**
 * Returns the numeric timestamp (ms since epoch) for a timeline entry.
 * Falls back to 0 when no timestamp is present so that entries without
 * a timestamp sort to the front.
 */
function getTimestamp(entry: ChatTimelineEntryDTO): number {
    if (isMessageEntry(entry)) {
        return entry.message.createdAt ? new Date(entry.message.createdAt).getTime() : 0;
    }
    if (isStatusPillEntry(entry)) {
        return entry.pill?.timestamp ? new Date(entry.pill.timestamp).getTime() : 0;
    }
    return 0;
}
